import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ThemeProvider,
  CircularProgress,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  LinearProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PersonIcon from "@mui/icons-material/Person";

import customTheme from "../../theme.js";
import API from "../../api/axiosConfig";
import EditableField from "../../components/EditableField";
import { useToast } from "../../context/ToastContext";
import ResumeUpload from "../../components/ResumeUpload.js";
import AutocompleteUsers from "../../components/AutocompleteUsers.js";

const booleanFields = [
  { label: "1. Candidate Information Entered", key: "information" },
  { label: "2. Resume Uploaded", key: "resume" },
  { label: "3. Matched With Position", key: "position" },
  { label: "4. Interview Completed", key: "interview" },
  { label: "5. Salary Negotiated", key: "salary" },
  { label: "6. Accepted to Position", key: "accepted" },
  { label: "7. Clearance Transferred", key: "clearanceTransferred" },
  { label: "8. Start Date Set", key: "startDate" },
  { label: "9. S3K Email Assigned", key: "emailGiven" },
  { label: "10. Welcome Email Sent", key: "welcome" },
  { label: "11. Insurance Completed", key: "insurance" },
];

export default function CandidateDetailPage() {
  const showToast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  const clearanceOptions = [
    { label: "No Clearance", value: "NONE" },
    { label: "Confidential", value: "CONFIDENTIAL" },
    { label: "Secret", value: "SECRET" },
    { label: "TS/SCI", value: "TS_SCI" },
  ];

  const polyOptions = [
    { label: "No Polygraph", value: "NONE" },
    { label: "Counter Intelligence", value: "COUNTER_INTELLIGENCE" },
    { label: "Full Scope", value: "FULL_SCOPE" },
  ];

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showCompletionButton, setShowCompletionButton] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editError, setEditError] = useState({});
  const [resumeFile, setResumeFile] = useState(null);
  const initialCandidateState = useRef(null);
  const [editData, setEditData] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [deleteResume, setDeleteResume] = useState(false);

  const areAllBooleansTrue = useCallback((candidateObj) => {
    if (!candidateObj) return false;
    return booleanFields.every((field) => candidateObj[field.key] === true);
  }, []);

  const handlePreview = async () => {
    try {
      const response = await API.get(
        `/api/candidates/resume/download/${candidate.id}`,
        {
          responseType: "blob",
        },
      );
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 3000);
    } catch (err) {
      if (err.response?.status !== 401) {
        showToast("Error opening preview.", "error");
      }
    }
  };

  useEffect(() => {
    if (candidate) {
      setEditData(candidate);
    }
  }, [candidate]);

  const handleResumeDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const response = await API.get(
        `/api/candidates/resume/download/${candidate.id}`,
        {
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", candidate.resumeFileName || "resume.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast("Could not download file", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleResumeChange = (file) => {
    setResumeFile(file);
    setEditData((prev) => ({ ...prev, resumeFileName: file?.name || null }));
    setDeleteResume(!file);
  };

  useEffect(() => {
    const fetchCandidate = async () => {
      setLoading(true);
      try {
        const response = await API.post(`/api/candidates/get/${id}`);
        const processedData = { ...response.data };
        booleanFields.forEach((field) => {
          if (
            typeof processedData[field.key] === "undefined" ||
            processedData[field.key] === null
          ) {
            processedData[field.key] = false;
          }
        });
        setCandidate(processedData);
        initialCandidateState.current = JSON.parse(
          JSON.stringify(processedData),
        );
        setShowCompletionButton(areAllBooleansTrue(processedData));
      } catch (err) {
        setError(
          err.response?.status === 404
            ? "Candidate not found"
            : "Unknown error occurred",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchCandidate();
  }, [id, areAllBooleansTrue]);

  // Helper to convert label or value to standard enum value
  const getEnumValue = (val, options) => {
    if (!val) return "NONE";
    const match = options.find(
      (o) =>
        o.value === val ||
        o.label === val ||
        o.label.toLowerCase() === val.toLowerCase(),
    );
    return match ? match.value : val.toUpperCase();
  };

  const handleCheckboxChange = (fieldName) => {
    setCandidate((prevCandidate) => {
      if (!prevCandidate) return prevCandidate;
      const updatedCandidate = {
        ...prevCandidate,
        [fieldName]: !prevCandidate[fieldName],
      };
      setHasChanges(
        JSON.stringify(updatedCandidate) !==
          JSON.stringify(initialCandidateState.current),
      );
      setShowCompletionButton(
        areAllBooleansTrue(initialCandidateState.current) &&
          areAllBooleansTrue(updatedCandidate),
      );
      return updatedCandidate;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEdits = async () => {
    setButtonLoading(true);
    const formData = new FormData();
    formData.append("id", id);

    if (!editData.name) {
      setEditError({ name: "Candidate name cannot be blank" });
      setButtonLoading(false);
      return;
    }
    if (!editData.email) {
      setEditError({ email: "Candidate email cannot be blank" });
      setButtonLoading(false);
      return;
    }

    // loop through edit data and append to formData
    Object.entries(editData).forEach(([key, value]) => {
      // Handle enum transformations
      if (key === "clearanceType") {
        formData.append(key, getEnumValue(value, clearanceOptions));
        return;
      }
      if (key === "polyType") {
        formData.append(key, getEnumValue(value, polyOptions));
        return;
      }

      // Prevent literal "null" or "undefined" strings in FormData
      const cleanValue =
        value !== null && value !== undefined ? String(value).trim() : "";

      formData.append(key, cleanValue);
    });

    if (resumeFile && !deleteResume) {
      formData.append("resume", resumeFile);
    }
    formData.append("deleteResume", deleteResume);

    try {
      await API.post("api/candidates/edit", formData);
      setIsEditing(false);
      setDeleteResume(false);
      setCandidate(editData);
      showToast("Profile updated successfully!", "success");
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data) {
        const { error, message } = err.response.data;
        showToast("Please fix validation errors", "error");

        if (error && message) {
          setEditError({ [error]: message });
        }
      } else {
        showToast("Error saving candidate.  Please try again.", "error");
      }
    } finally {
      setButtonLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!candidate) return;
    try {
      const response = await API.post("/api/candidates/update", {
        id: candidate.id,
        information: candidate.information,
        resume: candidate.resume,
        position: candidate.position,
        interview: candidate.interview,
        salary: candidate.salary,
        accepted: candidate.accepted,
        clearanceTransferred: candidate.clearanceTransferred,
        startDate: candidate.startDate,
        emailGiven: candidate.emailGiven,
        welcome: candidate.welcome,
        insurance: candidate.insurance,
      });
      setShowCompletionButton(areAllBooleansTrue(candidate));
      setCandidate(response.data);
      setHasChanges(false);
      showToast("Onboarding status updated!", "success");
    } catch (err) {
      setError("Error updating candidate status");
    }
  };

  const handleCompletion = async () => {
    try {
      await API.post("/api/candidates/complete", { id: candidate.id });
      showToast("Onboarding Completed!", "success");
      navigate("/candidates/view");
    } catch (err) {
      setError("Error completing candidate onboarding");
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setOpenDialog(true);
    } else {
      navigate("/candidates/view");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !candidate) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">
          {error || "No candidate data available."}
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/candidates/view")}
          sx={{ mt: 2 }}
        >
          Back to Candidates
        </Button>
      </Box>
    );
  }

  // Calculate Progress Percentage
  const completedCount = booleanFields.filter((f) => candidate[f.key]).length;
  const progressPercent = Math.round(
    (completedCount / booleanFields.length) * 100,
  );

  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "1400px", mx: "auto" }}>
        {/* Top Header / Action Bar */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              flexWrap="wrap"
              rowGap={1}
            >
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackClick}
              >
                Back
              </Button>

              {/* Candidate Name & Chips Aligned Side-by-Side */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                flexWrap="wrap"
                rowGap={1}
              >
                <Typography variant="h4" fontWeight="bold" sx={{ mr: 1 }}>
                  {candidate.name}
                </Typography>

                <Chip
                  label={`Clearance: ${candidate.clearanceType || "None"}`}
                  color="primary"
                  variant="filled"
                  sx={{
                    color: "#ffffff",
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                    height: "32px",
                    boxShadow: "0px 2px 4px rgba(0,0,0,0.15)",
                    px: 0.5,
                  }}
                />

                <Chip
                  label={`Poly: ${candidate.polyType || "None"}`}
                  color="secondary"
                  variant="filled"
                  sx={{
                    color: "#080808",
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                    height: "32px",
                    boxShadow: "0px 2px 4px rgba(0,0,0,0.15)",
                    px: 0.5,
                  }}
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2}>
              {hasChanges && !showCompletionButton && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdate}
                >
                  Save Onboarding Status
                </Button>
              )}

              {showCompletionButton && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<TaskAltIcon />}
                  onClick={handleCompletion}
                >
                  Complete Onboarding
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        {/* Main 2-Column Dashboard Layout */}
        <Grid container spacing={4}>
          {/* Left Column: Candidate Details Card */}
          <Grid item xs={12} md={5}>
            <Card elevation={3} sx={{ borderRadius: 2, height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                {/* Card Header */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Candidate Information
                    </Typography>
                  </Stack>

                  {!isEditing ? (
                    <Button
                      startIcon={<EditIcon />}
                      size="small"
                      variant="outlined"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                  ) : (
                    <Stack direction="row" spacing={1}>
                      <Button
                        startIcon={<SaveIcon />}
                        size="small"
                        color="success"
                        variant="contained"
                        disabled={buttonLoading}
                        onClick={handleSaveEdits}
                      >
                        Save
                      </Button>
                      <Button
                        startIcon={<CancelIcon />}
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => {
                          setIsEditing(false);
                          setEditData(candidate);
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <Divider sx={{ mb: 2.5 }} />

                {/* Main Fields Container — Left Aligned */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    textAlign: "left",
                    width: "100%",
                    gap: 2,

                    "& > *": {
                      width: "100%",
                      textAlign: "left",
                    },
                    "& .MuiTypography-root": {
                      textAlign: "left !important",
                      justifyContent: "flex-start !important",
                    },
                    "& .MuiStack-root, & .MuiBox-root": {
                      alignItems: "flex-start !important",
                      justifyContent: "flex-start !important",
                      textAlign: "left !important",
                    },
                    "& .MuiFormControl-root": {
                      width: "100%",
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                  >
                    Personal Information
                  </Typography>

                  {/* Candidate Email & S3K Email side-by-side */}
                  <Grid container spacing={2} sx={{ width: "100%", m: 0 }}>
                    <Grid item xs={12} sm={6} sx={{ pl: "0 !important" }}>
                      <EditableField
                        label="Candidate Name"
                        name="name"
                        value={editData.name}
                        error={editError.name}
                        isEditing={isEditing}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ pr: "0 !important" }}>
                      <EditableField
                        label="Personal Email"
                        name="email"
                        value={editData.email}
                        error={editError.email}
                        isEditing={isEditing}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ pl: "0 !important" }}>
                      <EditableField
                        label="Phone Number"
                        name="phoneNumber"
                        value={editData.phoneNumber || "None available"}
                        error={editError.phoneNumber}
                        isEditing={isEditing}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1, width: "100%" }} />

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                  >
                    S3K information
                  </Typography>

                  <Grid container spacing={2} sx={{ width: "100%", m: 0 }}>
                    <Grid item xs={12} sm={6} sx={{ pl: "0 !important" }}>
                      {isEditing ? (
                        <Box>
                          <Typography
                            sx={{
                              py: 2,
                              fontWeight: "bold",
                              textDecoration: "underline",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Referral Email :
                          </Typography>

                          <AutocompleteUsers
                            label="Referral Email: "
                            value={editData.referral || ""}
                            valueKey="email"
                            freeSolo
                            error={editError.referral}
                            helperText={editError.referral}
                            onChange={(selectedEmail) => {
                              setEditData((prev) => ({
                                ...prev,
                                referral: selectedEmail || "",
                              }));
                              if (editError.referral) {
                                setEditError((prev) => ({
                                  ...prev,
                                  referral: "",
                                }));
                              }
                            }}
                          />
                        </Box>
                      ) : (
                        <EditableField
                          label="Referral Email"
                          name="referral"
                          value={editData.referral || "No referral given"}
                          isEditing={false}
                        />
                      )}
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ width: "100%", m: 0 }}>
                    <Grid item xs={12} sm={6} sx={{ pl: "0 !important" }}>
                      <EditableField
                        label="S3K Email"
                        name="s3kEmail"
                        value={
                          isEditing
                            ? editData.s3kEmail || ""
                            : editData.s3kEmail || "Not given yet"
                        }
                        error={editError.s3kEmail}
                        isEditing={isEditing}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1, width: "100%" }} />

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                  >
                    Security Credentials
                  </Typography>

                  <Grid container spacing={2} sx={{ width: "100%", m: 0 }}>
                    <Grid item xs={12} sm={6} sx={{ pl: "0 !important" }}>
                      <EditableField
                        label="Clearance"
                        name="clearanceType"
                        value={editData.clearanceType}
                        isEditing={isEditing}
                        onChange={handleChange}
                        options={clearanceOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ pr: "0 !important" }}>
                      <EditableField
                        label="Polygraph"
                        name="polyType"
                        value={editData.polyType}
                        isEditing={isEditing}
                        onChange={handleChange}
                        options={polyOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ pl: "0 !important" }}>
                      <EditableField
                        label="Date of Polygraph"
                        name="polyDate"
                        value={editData.polyDate || "None given"}
                        isEditing={isEditing}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1, width: "100%" }} />

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                  >
                    Position Information
                  </Typography>

                  <Grid container spacing={2} sx={{ width: "100%", m: 0 }}>
                    <Grid item xs={12} sm={6} sx={{ pl: "0 !important" }}>
                      <EditableField
                        label="Desired Position"
                        name="desiredPosition"
                        value={
                          isEditing
                            ? editData.desiredPosition || ""
                            : editData.desiredPosition || "None given"
                        }
                        isEditing={isEditing}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ pl: "0 !important" }}>
                      <EditableField
                        label="Contract Placement"
                        name="contractPlacement"
                        value={
                          isEditing
                            ? editData.contractPlacement || ""
                            : editData.contractPlacement || "None given"
                        }
                        isEditing={isEditing}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ pl: "0 !important" }}>
                      <EditableField
                        label="Contract Role"
                        name="contractRole"
                        value={
                          isEditing
                            ? editData.contractRole || ""
                            : editData.contractRole || "None given"
                        }
                        isEditing={isEditing}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1, width: "100%" }} />

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                  >
                    Attachments
                  </Typography>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      width: "100%",
                      borderRadius: 2,
                      bgcolor: "action.hover",
                      borderStyle: "dashed",
                      boxSizing: "border-box",
                    }}
                  >
                    <ResumeUpload
                      isEditing={isEditing}
                      isDownloading={isDownloading}
                      deleteResume={deleteResume}
                      resumeFile={resumeFile}
                      fileName={editData.resumeFileName}
                      onFileSelected={handleResumeChange}
                      onFileDownload={handleResumeDownload}
                      handlePreview={handlePreview}
                    />
                  </Paper>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Onboarding Progress Tracker */}
          <Grid item xs={12} md={7}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box mb={3}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      Onboarding Progress
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      {completedCount} of {booleanFields.length} Tasks (
                      {progressPercent}%)
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    color={progressPercent === 100 ? "success" : "primary"}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>

                <Divider sx={{ mb: 2 }} />

                <List disablePadding sx={{ bgcolor: "background.paper" }}>
                  {booleanFields.map((field) => {
                    const isChecked = candidate[field.key];
                    return (
                      <ListItem
                        key={field.key}
                        onClick={() => handleCheckboxChange(field.key)}
                        sx={{
                          mb: 1,
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: isChecked ? "success.light" : "divider",
                          bgcolor: isChecked
                            ? "action.selected"
                            : "transparent",
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            checked={isChecked}
                            edge="start"
                            tabIndex={-1}
                            disableRipple
                            color="success"
                            checkedIcon={<CheckCircleOutlineIcon />}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={field.label}
                          primaryTypographyProps={{
                            fontWeight: isChecked ? "medium" : "regular",
                            color: isChecked
                              ? "text.primary"
                              : "text.secondary",
                            style: {
                              textDecoration: isChecked ? "none" : "none",
                            },
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Unsaved Changes Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You have unsaved changes on this candidate's onboarding status.
              Would you like to save before navigating away?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => navigate("/candidates/view")}
              color="error"
              variant="outlined"
            >
              Discard & Leave
            </Button>
            <Button
              onClick={async () => {
                await handleUpdate();
                navigate("/candidates/view");
              }}
              color="primary"
              variant="contained"
              autoFocus
            >
              Save & Exit
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
